import { isCustomDomain } from '@/components/AppStoreBadges';
import React, { useEffect, useState } from 'react';
import { X, CalendarDays, Gift, FileText, Phone, ShoppingBag, Heart, Settings, Ticket, TicketCheck, User, Bell, ShieldAlert, ChevronRight, PauseCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { createPageUrl } from '../utils';
import { toast } from 'sonner';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEventsPage } from '@/lib/useEventsPage';
import MobileMenuSheet from '@/components/MobileMenuSheet';

export default function SideDrawer({ isOpen, onClose, onNavigate, instant }) {
  const drawerRef = useFocusTrap(isOpen, onClose);
  const { isPublished: eventsPublished } = useEventsPage();
  const [contactInfo, setContactInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canManageEvents, setCanManageEvents] = useState(false);
  const [canPause, setCanPause] = useState(false);
  const [canAccessAdmin, setCanAccessAdmin] = useState(false);
  const [expandedGiftCards, setExpandedGiftCards] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(false);
  const [expandedOrdering, setExpandedOrdering] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 1027px)').matches);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1027px)');
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    base44.entities.ContactInfo.list().then((records) => {
      if (records.length > 0) setContactInfo(records[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isOpen) checkAdmin();
  }, [isOpen]);

  // Keep the browser toolbar white while the menu is open
  useEffect(() => {
    if (!isOpen) return;
    const meta = document.querySelector('meta[name="theme-color"]');
    const prev = meta?.getAttribute('content');
    meta?.setAttribute('content', '#ffffff');
    return () => { if (meta && prev) meta.setAttribute('content', prev); };
  }, [isOpen]);

  const normalisePhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('44')) return `+${digits}`;
    if (digits.startsWith('07')) return `+44${digits.slice(1)}`;
    return `+${digits}`;
  };

  const checkAdmin = async () => {
    let isBase44Admin = false;
    try {
      const user = await base44.auth.me();
      isBase44Admin = user?.role === 'admin';
      setCanManageEvents(user?.events_admin === true);
      setCanPause(user?.pause_admin === true);
    } catch (e) {
      isBase44Admin = false;
      setCanManageEvents(false);
    }
    setIsAdmin(isBase44Admin);

    if (isBase44Admin) {
      setCanAccessAdmin(false);
      return;
    }

    const customerData = localStorage.getItem('customer_data');
    if (customerData) {
      try {
        const data = JSON.parse(customerData);
        const adminConfig = await base44.entities.AdminConfig.list();
        if (adminConfig.length > 0) {
          const storedPhone = normalisePhone(data.phone_number);
          const isAdminPhone = storedPhone !== '' && adminConfig.some(
            (cfg) => normalisePhone(cfg.admin_phone_number) === storedPhone
          );
          setCanAccessAdmin(isAdminPhone);
        } else {
          setCanAccessAdmin(false);
        }
      } catch (e) {
        setCanAccessAdmin(false);
      }
    } else {
      setCanAccessAdmin(false);
    }
  };

  const handleLogout = async () => {
    try {
      await base44.auth.logout();
      setIsAdmin(false);
      onClose();
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  // Lets pull-to-refresh know the menu is open so it stays inactive.
  useEffect(() => {
    document.body.classList.toggle('drawer-open', isOpen);
    return () => document.body.classList.remove('drawer-open');
  }, [isOpen]);

  const handleLogin = () => {
    base44.auth.redirectToLogin();
  };

  const renderLink = (item) => (
    <Link
      key={item.page}
      to={createPageUrl(item.page)}
      onClick={() => { sessionStorage.setItem('cameFromMenu', '1'); if (item.nav) onNavigate?.(); onClose(); }}
      className="flex items-center gap-4 px-4 py-3 rounded-xl border border-gray-200 bg-white shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-colors">
      <item.icon className="w-5 h-5 flex-shrink-0 text-black" />
      <span className="font-medium text-black text-base flex-1">{item.label}</span>
      <ChevronRight className="w-5 h-5 flex-shrink-0 text-black" />
    </Link>
  );

  const nav = (
    <nav className="flex-1 px-4 pt-2 pb-6 space-y-2 overflow-y-auto">
      {[
        isAdmin && { label: 'Settings', page: 'AdminSettings', icon: Settings },
        { label: 'Allergens', page: 'Allergens', icon: ShieldAlert },
        { label: 'Book a Table', page: 'BookATable', icon: CalendarDays },
        { label: 'Contact Us', page: 'ContactUs', icon: Phone },
        canManageEvents && { label: 'Event Bookings', page: 'EventBookings', icon: TicketCheck },
        eventsPublished && { label: 'Events', page: 'Events', icon: Ticket },
        { label: 'Gift Cards', page: 'BuyGiftCard', icon: Gift },
        { label: 'Loyalty', page: 'Loyalty', nav: true, icon: Heart },
        { label: 'Menus', page: 'Menus', icon: FileText },
        isCustomDomain() && { label: 'My Account', page: 'Account', icon: User },
        isCustomDomain() && { label: 'Notifications', page: 'NotificationCenter', icon: Bell },
        { label: 'Order', page: 'Order', icon: ShoppingBag },
        (isAdmin || canPause) && { label: 'Pause Orders', page: 'PauseOrders', icon: PauseCircle },
      ].filter(Boolean).sort((a, b) => a.label.localeCompare(b.label)).map(renderLink)}
    </nav>
  );

  if (isMobile) {
    return (
      <MobileMenuSheet instant={instant} isOpen={isOpen} onClose={onClose} sheetRef={drawerRef}>
        {nav}
      </MobileMenuSheet>);
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen &&
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-white z-[65]"
        onClick={onClose} />

      }

      {/* Drawer - full screen. Hidden from assistive tech and from tab order
          while closed, so off-screen links are never reachable. */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        tabIndex={-1}
        aria-hidden={isOpen ? undefined : 'true'}
        inert={isOpen ? undefined : ''}
        className={`fixed inset-0 bg-white z-[65] transform transition-transform duration-300 flex flex-col ${
        isOpen ? 'translate-x-0' : 'translate-x-full invisible'}`
        }>
        
        {/* Close button */}
        <div className="flex items-center justify-between border-b border-gray-100 pt-4 pr-4 pl-4 pb-2">
          <span />

          <button onClick={onClose} aria-label="Close menu" className={`w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0 ${isCustomDomain() ? 'mt-0' : 'mt-8'} focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2 focus-visible:outline-none`} style={{ backgroundColor: '#111827' }}>
            <X aria-hidden="true" className="w-5 h-5 text-white" />
          </button>
        </div>

        {nav}
      </div>
    </>);

}